import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type {CategorizationReviewPageController} from "./CategorizationReviewPageController.ts";
import type {MuiXGroupedResponse, MuiXResponse} from "~/components/muix-data-grid/MuixApiTypes.ts";
import type {PartGridRowModel} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import type {MatPartReviewRowResp, MatReviewAggregatedGroupData} from "~/services/classes/MaterializedClasses.ts";
import type {ApiCategory, Category, PartialCategory, PartialValidCategory} from "~/services/classes/Category.ts";
import type {
    GridAggregationModel,
    GridFilterModel,
    GridGetRowsParams,
    GridRowGroupingModel,
    GridRowId
} from "@mui/x-data-grid-premium";
import {GridLogicOperator} from "@mui/x-data-grid-premium";
import {EMPTY_FILTER} from "~/components/muix-data-grid/MuixDataGridConstants.ts";
import type {AxiosResponse} from "axios";
import axios from "axios";
import {catchError, finalize, from, map, Subject} from "rxjs";
import {PipeManager} from "~/stores/managers/PipeManager.tsx";
import {rollbar} from "~/env.ts";
import {deepCopy} from "~/utils/js-utils.ts";

export type MuiXPartResponse = MuiXResponse<MatPartReviewRowResp>
export type MuiXGroupResponse = MuiXGroupedResponse<MatReviewAggregatedGroupData>

const url = '/m_review/base/muix/grid/';
const url_many = '/m_review/base/muix/update_many/';

declare type StorePartNormalData = {
    update_part_ids?: string[] | number[]
    update_supplier_ids?: string[] | number[]
    cat?: PartialCategory | null
}
declare type StorePartManyData = {part_id: number, cat: Category}[]
declare type StorePartReviewPipeElement = {params: URLSearchParams} & ({
    type: 'normal'
    data: StorePartNormalData
} | {
    type: 'many',
    data: StorePartManyData
});

export class CategorizationReviewApi {
    constructor(
        private controller: CategorizationReviewPageController,
        private materializedApi: MithraMaterializedApi,
    ) {
        this.cancelUpdateSubject.subscribe(() => {
            console.warn('Abandoning part updates is not implemented yet');
            // this.asyncPartUpdatePipe.reset()
            // this.asyncPartMultiUpdatePipe.reset()
        })
    }

    get nInPipe() {
        return this.updatePipe.nInPipe + this.getPipe.nInPipe;
    }

    public static getMuiXQuery(
        databag: number,
        revision: number | undefined,
        data: Partial<{
            sortModel: GridGetRowsParams['sortModel'],
            filterModel: GridGetRowsParams['filterModel'] | null,
            groupKeys: GridGetRowsParams['groupKeys'],
            groupFields: GridGetRowsParams['groupFields'],
            aggregationModel: GridGetRowsParams['aggregationModel'],
            relatedData: Record<string, string[]>,
            page: number,
            pageSize: number,
            noContent: boolean,
            noPage: boolean,
        }>,
        filterOnPartIds?: number[],
        filterOnSupplierIds?: GridRowId[],
    ): URLSearchParams {
        if (EMPTY_FILTER.items.length > 0) {
            throw new Error("EMPTY_FILTER has been modified");
        }

        const filterModel = data.filterModel ?? deepCopy(EMPTY_FILTER);
        if (filterOnPartIds && filterOnPartIds.length > 0) {
            // Add a additional filter for the ids
            filterModel.items = [
                ...filterModel.items.filter(item => item.field !== 'part_id'),
                {
                    id: filterModel.items.length > 0 ? Math.max(...filterModel.items.map(i => Number(i.id))) + 1 : 1,
                    field: 'part_id',
                    operator: filterOnPartIds.length === 1 ? 'is' : 'isAnyOf',
                    value: filterOnPartIds.length === 1 ? filterOnPartIds[0] : filterOnPartIds,
                }
            ];
            if (filterModel.logicOperator === undefined) {
                filterModel.logicOperator = GridLogicOperator.And;
            }
        }
        if (filterOnSupplierIds && filterOnSupplierIds.length > 0) {
            filterModel.items = [
                ...filterModel.items.filter(item => item.field !== 'supplier_id'),
                {
                    id: filterModel.items.length > 0 ? Math.max(...filterModel.items.map(i => Number(i.id))) + 1 : 1,
                    field: 'supplier_id',
                    operator: filterOnSupplierIds.length === 1 ? 'is' : 'isAnyOf',
                    value: filterOnSupplierIds.length === 1 ? filterOnSupplierIds[0] : filterOnSupplierIds,
                }
            ];
            if (filterModel.logicOperator === undefined) {
                filterModel.logicOperator = GridLogicOperator.And;
            }
        }

        const query = new URLSearchParams();
        query.set('databag', String(databag));
        if (revision)
            query.set('revision_id', String(revision));
        query.set('filterModel', JSON.stringify(filterModel));
        query.set('sortModel', data.sortModel ? JSON.stringify(data.sortModel) : '[]');
        query.set('groupKeys', data.groupKeys ? JSON.stringify(data.groupKeys) : '[]'); // pro
        query.set('groupFields', data.groupFields ? JSON.stringify(data.groupFields) : '[]'); //  premium
        query.set('aggregationModel', data.aggregationModel ? JSON.stringify(data.aggregationModel) : '{}'); // premium
        if (data.relatedData !== undefined) query.set('related_data', JSON.stringify(data.relatedData));
        if (data.page !== undefined) query.set('page', String(data.page));
        if (data.pageSize !== undefined) query.set('page_size', String(data.pageSize));
        if (data.noContent) query.set('noContent', 'true');
        if (data.noPage) query.set('no_page', '');
        return query;
    }

    /**
     * Root-level grid requests and each expanded subgroup's requests are independent fetch "channels".
     * Two requests within the same channel are never wanted concurrently (a newer one always supersedes
     * an older one, e.g. rapid filter/search/sort changes), so the previous in-flight request for a
     * channel is aborted when a new one for that same channel starts.
     */
    private readonly pendingGetControllers = new Map<string, AbortController>();

    /**
     * Get all data (paginated)
     */
    get(
        databag: number,
        revision: number | undefined,
        params: GridGetRowsParams,
        group_by: string | null,
        group_key: string | null,
        filter: GridFilterModel | null,
        relatedData: Record<string, string[]>,
    ): Promise<AxiosResponse<MuiXGroupResponse> | AxiosResponse<MuiXPartResponse>> {
        let pageSize: number = this.controller.reviewPageTableController.muixCustomizedPaginationRowsPerPage;
        let pageIndex: number = this.controller.reviewPageTableController.muixCustomizedPaginationPage + 1;
        if (group_by && group_key) {
            // We are requesting a subgroup, pagination should be fetched from group override value
            pageSize = this.controller.reviewPageTableController.getGroupPerPage(group_key);
            pageIndex = this.controller.reviewPageTableController.getGroupPage(group_key) + 1;
        }

        const query = CategorizationReviewApi.getMuiXQuery(databag, revision, {
            sortModel: params.sortModel,
            // Handle the complex filters in the backend
            filterModel: filter,
            groupKeys: params.groupKeys,
            groupFields: params.groupFields,
            aggregationModel: params.aggregationModel,
            relatedData,
            page: pageIndex,
            pageSize: pageSize,
        });

        // Cancel the previous request for this same channel (root grid, or a specific expanded subgroup)
        const channel = group_key ?? '__root__';
        this.pendingGetControllers.get(channel)?.abort();
        const controller = new AbortController();
        this.pendingGetControllers.set(channel, controller);

        const key = `get-${query}}`
        const beforeReject = (reason) => {
            if (axios.isCancel(reason)) {
                // Expected: this request was superseded by a newer one for the same channel
                return;
            }
            console.error('CategorizationReviewApi.get failed! reason: ', reason);
            if (axios.isAxiosError(reason) && reason.response?.data['detail'] === 'Invalid page.') {
                if (group_key)
                    this.controller.setPaginationStateInvalidSupplierId(group_key)
                else
                    this.controller.setPaginationStateInvalid(true);
            }
        }
        return (this.getPipe.processAndPromise(key, {
            params: query,
            signal: controller.signal
        }, beforeReject) as Promise<AxiosResponse<MuiXGroupResponse> | AxiosResponse<MuiXPartResponse>>)
            .finally(() => {
                if (this.pendingGetControllers.get(channel) === controller)
                    this.pendingGetControllers.delete(channel);
            });
    }

    /**
     * Get the data for a finite list of ids
     * @return all data for the given ids (not paginated)
     */
    getByPartIds(databag: number, revision: number | undefined, part_ids: number[]): Promise<PartGridRowModel[]> {
        if (part_ids.length === 0) console.warn('getDataByIds called with empty part_ids array');
        if (part_ids.length > 1000) console.warn('getDataByIds is meant for small selections only!');
        // Store the related fields at time of query
        const relatedData = this.controller.relatedDataFieldsSpec;
        const params = CategorizationReviewApi.getMuiXQuery(databag, revision, {
            relatedData,
            noPage: true,
        }, part_ids);
        // const key = `get-${query}}`
        const key = `get-row-part-id-${part_ids.join('-')}`
        return this.getPipe.processAndPromise(key, {params})
            .then(response => (response as AxiosResponse<Array<MatPartReviewRowResp>>).data.map(
                row => this.controller.reviewPageTableController.toPartModel(row, relatedData)
            ))
    }

    /**
     * Get the data for a finite list of groups, intended for fetching one supplier aggregated data,
     *  when refresh button for out-of-sync supplier is pressed.
     *  Note that aggregation is being performed on DB and one supplier row is returned
     * @return all data for the given ids (not paginated)
     */
    getBySupplierId(databag: number, revision: number | undefined,
        groupFields: GridRowGroupingModel,
        aggregationModel: GridAggregationModel,
        supplier_id: GridRowId): Promise<AxiosResponse<MatReviewAggregatedGroupData[]>> {
        const params = CategorizationReviewApi.getMuiXQuery(databag, revision, {
            groupFields,
            aggregationModel,
            noPage: true,
        }, undefined, [supplier_id]);
        const key = `get-row-supplier-id-${supplier_id}`
        return this.getPipe.processAndPromise(key, {params}) as Promise<AxiosResponse<MatReviewAggregatedGroupData[]>>;
    }

    /**
     * Update all data matching the filter to a single category, or accepting
     */
    updateAll(
        databag: number,
        revision: number | undefined,
        filter: GridFilterModel | null,
        category: PartialValidCategory | ApiCategory | null,
    ) {
        const relatedData = this.controller.relatedDataFieldsSpec;
        // Only filter
        const params = CategorizationReviewApi.getMuiXQuery(databag, revision, {
            filterModel: filter,
            relatedData,
            noContent: true,
        });
        const data: StorePartNormalData = {
            cat: category ?? undefined,
            // update_ids NOT used here
            // update_supplier_ids NOT used here
        }
        const key = 'all-' + JSON.stringify(filter);
        return this.updatePipe.processAndPromise(key, {type: 'normal', data, params})
    }

    updateRow(
        databag: number,
        revision: number | undefined,
        part_id: number,
        category: Category | ApiCategory | PartialValidCategory | null,
    ) {
        const relatedData = this.controller.relatedDataFieldsSpec;
        const params = CategorizationReviewApi.getMuiXQuery(Number(databag), revision, {
            relatedData,
            noPage: true,
        });
        const data: StorePartNormalData = {
            update_part_ids: [part_id],
            cat: category ?? undefined,
        }
        const key = 'row-' + (data.update_part_ids ?? []).join('-')
        return this.updatePipe.processAndPromise(key, {type: 'normal', data, params})
    }

    /**
     * Used for updating multiple selected rows to one category
     * Clicked the new category for all selected rows; parts, suppliers or supplier->parts
     */
    updateByIds(
        databag: number,
        revision: number | undefined,
        filter: GridFilterModel | null,
        part_ids: Array<number>,
        supplier_ids: Array<number>,
        category: Category | ApiCategory | PartialValidCategory | null,
    ) {
        const relatedData = this.controller.relatedDataFieldsSpec;
        const params = CategorizationReviewApi.getMuiXQuery(Number(databag), revision, {
            filterModel: filter,
            relatedData,
            noContent: true,
        });
        // Length of request could get higher than limitation of web server. Pass ids in body
        const data: StorePartNormalData = {
            update_part_ids: part_ids,
            update_supplier_ids: supplier_ids,
        }
        if (category)
            data['cat'] = category;
        const key = `row-part-${data.update_part_ids?.join('-')}-sup-${data.update_supplier_ids?.join('-')}`
        return this.updatePipe.processAndPromise(key, {type: 'normal', data, params})
    }

    /**
     * Update many rows at once, each with their own category
     */
    updateManyRowsPipe(databag: number, revision: number | undefined, data: StorePartManyData) {
        const relatedData = this.controller.relatedDataFieldsSpec;
        const params = CategorizationReviewApi.getMuiXQuery(databag, revision, {relatedData});
        const key = `row-many-id-${data.map(d => d.part_id).join('-')}`
        return this.updatePipe.processAndPromise(key, {type: 'many', data, params})
    }

    /**
     * Update many rows at once, each with their own category
     */
    updateManyRowsSync(
        databag: number,
        revision: number | undefined,
        data: Array<StorePartManyData>,
    ) {
        const relatedData = this.controller.relatedDataFieldsSpec;
        const params = CategorizationReviewApi.getMuiXQuery(databag, revision, {relatedData});
        return this.materializedApi.http.patch<void>(url_many, data, {params})
    }

    private readonly cancelUpdateSubject: Subject<void> = new Subject();
    private readonly updatePipe = new PipeManager<StorePartReviewPipeElement, void, string>(
        ({type, data, params}) => {
            console.log('updatePipe', type, data, params);
            let s, _url;
            if (type === 'normal') {
                _url = url;
                s = String((data as StorePartNormalData).update_part_ids?.at(0) ?? null);
            } else {
                _url = url_many;
                s = (data as StorePartManyData).map(d => d.part_id).join('-');
            }
            // Then call the API
            // noinspection TypeScriptValidateTypes
            return from(this.materializedApi.http.patch(_url, data, {params})).pipe(
                map(() => {
                    console.log('Updated review of part', s);
                }),
                catchError(err => {
                    // noinspection TypeScriptValidateTypes
                    rollbar.error('Cannot store review of part', s, err)
                    throw err;
                }),
                finalize(() => {
                    console.log('Request finished of part', s);
                }),
            );
        }
    )
    private readonly getPipe = new PipeManager<{params: URLSearchParams, signal?: AbortSignal}, unknown, string>(
        ({params, signal}) => from(this.materializedApi.http.get(url, {params, signal})),
    )
}
